/**
 * ポータル: 掲示板・回覧（船員アプリ）
 * 自分宛てのお知らせ・回覧を読む。回覧は「読みました」で既読を記録する。作成は陸上から。
 */
import { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { PIcon } from '../../components/PIcon';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import { PillBadge, MarkdownView, fmtDateTime, fileUrl, EmptyState } from './ui';

interface Post {
  id: string; post_type: string; title: string; body: string; pinned: number;
  deadline: string | null; require_read: number; author_name: string | null;
  created_at: string; my_read: boolean; attachment_count: number;
  attachments?: Array<{ id: string; file_name: string }>;
}

export default function PortalBoardPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [tab, setTab] = useState<'all' | 'announcement' | 'circular'>('all');
  const [detail, setDetail] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<{ data: Post[] }>('/portal/board');
      setPosts(r.data ?? []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const open = async (id: string) => {
    try {
      const r = await apiFetch<{ data: Post }>(`/portal/board/${id}`);
      setDetail(r.data);
      if (!r.data.my_read) {
        // 既読の送信は圏外だと失敗するが、閲覧は続けられるようにする（次にオンラインで開いたとき記録される）
        apiFetch(`/portal/board/${id}/read`, { method: 'POST', body: JSON.stringify({}) })
          .then(() => setPosts(ps => ps.map(p => p.id === id ? { ...p, my_read: true } : p)))
          .catch(() => { /* 圏外 */ });
      }
    } catch (e) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const filtered = posts.filter(p => tab === 'all' || p.post_type === tab);

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="掲示板・回覧">
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 12, padding: 4 }}>
          {([['all', 'すべて'], ['announcement', 'お知らせ'], ['circular', '回覧']] as const).map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                background: tab === key ? 'white' : 'transparent',
                color: tab === key ? '#0f172a' : '#64748b',
                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{lbl}</button>
          ))}
        </div>
      </AppHeader>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 90px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>読み込み中...</p>
        ) : filtered.length === 0 ? (
          <EmptyState img="/portal/empty-board.png" text="投稿はまだありません" />
        ) : filtered.map(p => (
          <button key={p.id} onClick={() => void open(p.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
              background: p.my_read ? '#fff' : '#eef6f3',
              border: p.my_read ? '1.5px solid #e4e9f0' : '1.5px solid #cfe8e4',
              borderRadius: 14, padding: '13px 14px', cursor: 'pointer',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
              {p.post_type === 'circular'
                ? <PillBadge color="#b45309" bg="#fef3c7">回覧</PillBadge>
                : <PillBadge color="#0f766e" bg="#e6f4f1">お知らせ</PillBadge>}
              {p.pinned === 1 && <PIcon name="pin" size={13} color="#d93025" />}
              {!p.my_read && <PillBadge color="#dc2626" bg="#fee2e2">未読</PillBadge>}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>{fmtDateTime(p.created_at)}</span>
            </div>
            <div style={{ fontSize: 14.5, fontWeight: p.my_read ? 600 : 800, color: '#0f172a' }}>
              {p.title}{p.attachment_count > 0 && <span style={{ display: 'inline-block', verticalAlign: -2, marginLeft: 6 }}><PIcon name="paperclip" size={13} color="#0c5f59" /></span>}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              {p.author_name}
              {p.deadline && <span style={{ color: '#b45309', marginLeft: 8 }}>確認期限 {p.deadline.replace(/-/g, '/')}</span>}
            </div>
          </button>
        ))}
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '86dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              {detail.post_type === 'circular'
                ? <PillBadge color="#b45309" bg="#fef3c7">回覧</PillBadge>
                : <PillBadge color="#0f766e" bg="#e6f4f1">お知らせ</PillBadge>}
              <button onClick={() => setDetail(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>{detail.title}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              {detail.author_name} ・ {fmtDateTime(detail.created_at)}
              {detail.deadline && <span style={{ color: '#b45309', marginLeft: 8 }}>確認期限 {detail.deadline.replace(/-/g, '/')}</span>}
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
              <MarkdownView text={detail.body} />
            </div>
            {(detail.attachments?.length ?? 0) > 0 && detail.attachments!.map(a => (
              <a key={a.id} href={fileUrl(`/portal/board/attachments/${a.id}`)} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#0c5f59', padding: '6px 0', fontWeight: 700 }}>
                <PIcon name="paperclip" size={13} /> {a.file_name}
              </a>
            ))}
            <div style={{ marginTop: 10, fontSize: 13, color: '#15803d', fontWeight: 700 }}>✓ 既読として記録しました</div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
